import React from 'react';

import Store from '@app/store';

// WARNING: don't use this HOC to wrap TransitionGroup components.
// https://github.com/reactjs/react-transition-group/issues/18
// This component will hide some of the functionality exposed
// and needed by react-transition-group. Fancy animations killed Analytics 😱

/**
 * HOC to decorate a component with analytics events.
 * @param  {Object}   valueObservers    A list of store change observers
 * @param  {Object}   actionListeners   A list of action dispatched observers
 * @param  {Object}   methodListeners   A list of methods to override on BaseComponent
 * @param  {Boolean}  invokeImmediately Invoke observers inmediately?
 * @return {Function}                   Decorated component. Yay!
 */
export default (
  valueObservers = {},
  actionListeners = {},
  methodListeners = {},
  invokeImmediately = true
) => (BaseComponent) => {
  class WithAnalytics extends React.Component {
    /**
     * Register observers for Store value changes
     * Register listeners for dispatched actions
     */
    UNSAFE_componentWillMount() {
      this.unsubscribe = Store.stateChangeListener(
        valueObservers,
        invokeImmediately
      );
      this.removeActionListenerList = Object.keys(actionListeners).map(
        (actionType) =>
          Store.addActionListener(actionType, actionListeners[actionType])
      );
    }

    /**
     * Override original BaseComponents methods with analytics stuff
     */
    componentDidMount() {
      Object.keys(methodListeners).forEach((methodName) => {
        const originalMethod = this.baseComponent[methodName];
        this.baseComponent[methodName] = (...args) => {
          // Execute Analytics stuff
          methodListeners[methodName].apply(Store.getState(), args);
          // Call the original method
          return originalMethod.apply(this.baseComponent, args);
        };
      });
    }

    /**
     * Remove all listeners and components
     */
    componentWillUnmount() {
      if (this.unsubscribe) {
        this.unsubscribe();
      }

      if (this.removeActionListenerList) {
        this.removeActionListenerList.forEach((removeListener) =>
          removeListener()
        );
      }
    }

    render() {
      return (
        <BaseComponent
          ref={(baseComponent) => (this.baseComponent = baseComponent)}
          {...this.props}
        />
      );
    }
  }

  const displayName =
    BaseComponent.displayName || BaseComponent.name || 'Component';

  WithAnalytics.displayName = `WithAnalytics[${displayName}]`;

  return WithAnalytics;
};
